'use client';

import * as RadixForm from '@radix-ui/react-form';
import {
  RadixRadioGroup,
  RadixRadioItem,
} from '@tuic/web-compositions/core/RadixRadio';
import {
  InputFieldError,
  InputFieldLabel,
  InputFieldNote,
} from '@tuic/web-compositions/shared/InputField';

import type { AnyFieldApi } from '@tanstack/react-form';
import type { FC, ReactNode } from 'react';

type RadioOption = {
  value: string;
  label: string;
};

export type ManifestRadioFieldProps = {
  field: AnyFieldApi;
  label?: ReactNode;
  note?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  options: RadioOption[];
};

export const ManifestRadioField: FC<ManifestRadioFieldProps> = ({
  field,
  label,
  note,
  required,
  disabled,
  className,
  options,
}) => {
  const value = (field.state.value ?? '') as string;
  const error = field.state.meta.errors.map(e => e?.message).join(',');

  return (
    <RadixForm.Field name={field.name} className={className} asChild>
      <div className="space-y-1">
        {label && (
          <RadixForm.Label asChild>
            <InputFieldLabel asChild>
              <div>
                {label}
                {required ? '*' : ''}
              </div>
            </InputFieldLabel>
          </RadixForm.Label>
        )}
        <RadixRadioGroup
          value={value}
          onValueChange={v => field.handleChange(v)}
          disabled={disabled}
          className="flex items-center gap-7"
        >
          {options.map(option => (
            <RadixRadioItem
              key={option.value}
              id={`${field.name}-${option.value}`}
              value={option.value}
              label={option.label}
            />
          ))}
        </RadixRadioGroup>
        {error ? (
          <InputFieldError>{error}</InputFieldError>
        ) : (
          note && <InputFieldNote>{note}</InputFieldNote>
        )}
      </div>
    </RadixForm.Field>
  );
};
